import type { Resolution, RgbaString, RgbString } from '../types';
import { aspectRatioConfigurations, type AspectRatio } from '../aspectRatioConfig';

export function getReferenceResolution(aspectRatio: string): Resolution {
    return aspectRatioConfigurations[aspectRatio as AspectRatio].referenceResolution;
}

export function getResolution(resolutionValue: number, aspectRatio: string): Resolution {
    return aspectRatioConfigurations[aspectRatio as AspectRatio].getResolution(resolutionValue);
}

/**
 * Returns the minimum dimension, determined by the height of a video and its aspect ratio.
 */
export function getResolutionValueFromVideoHeight(height: number, aspectRatio: string): number {
    return aspectRatioConfigurations[aspectRatio as AspectRatio].getResolutionValueFromVideoHeight(
        height,
    );
}

/**
 * Convert a RGB array using the 0 to 255 range into a hex color value
 * @param rgb - rgb colour
 */
export function rgb2int(rgb: number[]): number {
    if (rgb.length !== 3 && rgb.length !== 4) {
        throw Error(`Expected 3 or 4 values, got ${rgb.length}`);
    }
    return (rgb[0] << 16) + (rgb[1] << 8) + (rgb[2] | 0);
}

export function normalizedRgb2int(rgb: number[]): number {
    return rgb2int(rgb.map((value) => value * 255));
}

export function int2rgbArray(value: number): Uint8ClampedArray {
    const array = new Uint8ClampedArray(3);
    array[0] = (value >> 16) & 0xff;
    array[1] = (value >> 8) & 0xff;
    array[2] = value & 0xff;
    return array;
}

export function int2NormalizedRgbArray(value: number): number[] {
    const rgbArray = int2rgbArray(value);
    return [rgbArray[0] / 255, rgbArray[1] / 255, rgbArray[2] / 255];
}

/**
 * Converts a hex colour value into an CSS formatted RGB string: rgb(r, g, b)
 * @param rgbNum
 */
export function int2rgb(rgbNum: number): RgbString {
    const [r, g, b] = int2rgbArray(rgbNum);
    return `rgb(${r},${g},${b})`;
}

/**
 * Converts a hex colour and alpha value into an CSS formatted RGBA string: rgba(r, g, b, a)
 */
export function int2rgba(rgbNum: number, alpha: number): RgbaString {
    const [r, g, b] = int2rgbArray(rgbNum);
    return `rgba(${r},${g},${b},${alpha})`;
}

export function string2rgbaArray(rgbaString: RgbaString) {
    const rgb = rgbaString.replace(/[^\d,]/g, '').split(',');
    return rgb.map((value) => parseInt(value, 10)) as [number, number, number, number];
}

export function rgbaStringToRgbString(rgbaString: RgbaString): RgbString {
    const rgbArray = string2rgbaArray(rgbaString).slice(0, 3);
    return rgbArray2String(rgbArray) as RgbString;
}

/**
 * Convert a RGB(A) array using the 0 to 255 range into a CSS color string such as "rgba(0,0,0,1)"
 * @param rgbaArray - rgb or rgba array
 */
export function rgbArray2String(rgbaArray: number[]): RgbString | RgbaString {
    if (rgbaArray.length === 4) {
        return `rgba(${rgbaArray.join(', ')})` as RgbaString;
    } else if (rgbaArray.length === 3) {
        return `rgb(${rgbaArray.join(', ')})` as RgbString;
    }
    throw Error(`Expected either 3 or 4 values, got ${rgbaArray.length}`);
}

export function rgbArray2hexString(rgbaArray: number[]): string {
    let r = rgbaArray[0].toString(16);
    let g = rgbaArray[1].toString(16);
    let b = rgbaArray[2].toString(16);
    if (r.length === 1) {
        r = '0' + r;
    }
    if (g.length === 1) {
        g = '0' + g;
    }
    if (b.length === 1) {
        b = '0' + b;
    }
    return '#' + r + g + b;
}
